import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkIcon from "@mui/icons-material/Work";
import moment from "moment";

import { Invoice } from "../admin/types";
import { formatCurrencyAmount } from "../common/utils";
import { primary, borders } from "../theme/theme";

const formatShortDate = (date: Date): string => moment(date).format("ll");

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  bgColor: string;
}

const StatCard = ({ icon, label, value, color, bgColor }: StatCardProps) => (
  <Box
    sx={{
      p: 2.5,
      borderRadius: 2,
      backgroundColor: bgColor,
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          backgroundColor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
    <Typography variant="h5" fontWeight={600} sx={{ color }}>
      {value}
    </Typography>
  </Box>
);

interface DashboardEarningsStatsProps {
  engagements: any[];
  invoices: Invoice[];
  user: any;
}

function DashboardEarningsStats(props: DashboardEarningsStatsProps) {
  const { engagements, invoices, user } = props;

  const activeEngagements = engagements.filter((e: any) => !e.endDate);
  const numberOfHoursBooked = activeEngagements.reduce(
    (prev: number, curr: any) => prev + curr.numberOfHoursPerWeek,
    0
  );

  const currency = invoices && invoices.length ? invoices[0].currency : "USD";
  const totalEarningsAmount = (invoices || []).reduce(
    (total: number, invoice: Invoice) => total + invoice.totalPaymentAmount,
    0
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: borders.light,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Earnings Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.createdAt
            ? `Since ${formatShortDate(user.createdAt)}`
            : "Your earnings summary"}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<TrendingUpIcon sx={{ color: "white", fontSize: 20 }} />}
            label="Total Earnings"
            value={formatCurrencyAmount(totalEarningsAmount, currency)}
            color={primary.main}
            bgColor="#e0ecff"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<AccessTimeIcon sx={{ color: "white", fontSize: 20 }} />}
            label="Hours / Week"
            value={`${numberOfHoursBooked} hrs`}
            color="#FF9800"
            bgColor="#FFF3E0"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<WorkIcon sx={{ color: "white", fontSize: 20 }} />}
            label="Active Engagements"
            value={activeEngagements.length}
            color="#00D88E"
            bgColor="#E8F5E9"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default DashboardEarningsStats;
